import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { COLLECTION } from './data';

// COLLECTION — archive grid. NO hero. Sticky filter rail.
export default function Collection() {
  const [brand, setBrand] = useState('All');
  const [decade, setDecade] = useState('All');
  const [sort, setSort] = useState('newest');
  const [active, setActive] = useState(null);

  const brands = ['All', ...new Set(COLLECTION.map(w => w.brand))];
  const decades = ['All', ...Array.from(new Set(COLLECTION.map(w => w.decade))).sort()];

  const filtered = useMemo(() => {
    let r = [...COLLECTION];
    if (brand !== 'All') r = r.filter(w => w.brand === brand);
    if (decade !== 'All') r = r.filter(w => w.decade === decade);
    if (sort === 'price-low') r.sort((a, b) => a.price - b.price);
    else if (sort === 'price-high') r.sort((a, b) => b.price - a.price);
    else r.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    return r;
  }, [brand, decade, sort]);

  return (
    <div className="bg-[#F2EBDA] text-[#0E0E0E]">
      {/* Page title strip — NOT a hero */}
      <div className="border-b border-[#0E0E0E]/10">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-12 py-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B2D26] mb-3">— Section 02 / Catalogue</p>
            <h1 className="font-serif text-5xl lg:text-7xl leading-[0.95]">The <span className="italic text-[#7B2D26]">collection</span>.</h1>
          </div>
          <p className="font-mono text-[11px] text-[#0E0E0E]/55 max-w-md leading-relaxed">{filtered.length} pieces available. All prices in Swiss francs, exclusive of VAT. Documentation and provenance on request.</p>
        </div>
      </div>

      {/* Filter rail — sticky on desktop, horizontal scroll on mobile */}
      <div className="sticky top-[68px] z-30 bg-[#F2EBDA] border-b border-[#0E0E0E]/10">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-12 py-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] font-mono uppercase tracking-[0.18em]">
          <FilterGroup label="Maison" options={brands} value={brand} onChange={setBrand} testId="watch-filter-brand" />
          <FilterGroup label="Decade" options={decades} value={decade} onChange={setDecade} testId="watch-filter-decade" />
          <div className="flex items-center gap-3 ml-auto">
            <span className="text-[#0E0E0E]/45">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="watch-filter-sort" className="bg-transparent border border-[#0E0E0E]/20 px-3 py-1.5 outline-none focus:border-[#7B2D26] text-[10px] uppercase tracking-[0.2em] font-mono">
              <option value="newest">Newest first</option>
              <option value="price-low">Price ↑</option>
              <option value="price-high">Price ↓</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1440px] mx-auto px-8 lg:px-12 py-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#0E0E0E]/8">
        {filtered.map((w, i) => (
          <motion.button key={w.ref + w.year} onClick={() => setActive(w)} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
            className="bg-[#F2EBDA] p-5 lg:p-7 group text-left hover:bg-[#F8F2E2] transition-colors duration-500" data-testid={`watch-piece-${w.ref}`}>
            <div className="aspect-square bg-[#FAF6E8] overflow-hidden mb-5 p-6"><img src={w.img} alt={w.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" loading="lazy" style={{ cursor: 'zoom-in' }} /></div>
            <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#0E0E0E]/45">{w.brand}</p>
            <p className="font-serif text-base mt-1 leading-tight">{w.name}</p>
            <div className="font-mono text-[10px] text-[#0E0E0E]/55 mt-2 space-y-0.5">
              <p>Ref. {w.ref} &middot; {w.year}</p>
              <p>{w.case}</p>
            </div>
            <div className="border-t border-[#0E0E0E]/10 mt-4 pt-3 flex items-center justify-between">
              <span className="font-serif text-lg text-[#7B2D26]">CHF {w.price.toLocaleString()}</span>
              <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#0E0E0E]/40 group-hover:text-[#7B2D26]">Detail →</span>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Detail modal */}
      {active && (
        <div onClick={() => setActive(null)} className="fixed inset-0 z-50 bg-[#0E0E0E]/85 flex items-center justify-center p-6 cursor-zoom-out">
          <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#F2EBDA] max-w-[1100px] w-full max-h-[88vh] overflow-y-auto grid lg:grid-cols-2 gap-0">
            <div className="aspect-square bg-[#F8F2E2] p-12 lg:p-20"><img src={active.img} alt={active.name} className="w-full h-full object-cover" /></div>
            <div className="p-10 lg:p-14 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B2D26]">{active.brand} &middot; Ref. {active.ref}</p>
                <button onClick={() => setActive(null)} className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0E0E0E]/50 hover:text-[#0E0E0E]">Close ✕</button>
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl leading-tight">{active.name}</h2>
              <table className="font-mono text-[11px] mt-8 w-full">
                <tbody>
                  {[['Year', active.year], ['Decade', active.decade], ['Case', active.case], ['Movement', active.mvt], ['Provenance', 'Single owner, full documentation'], ['Condition', '9.4 / 10 — original parts, no service marks']].map(([k, v]) => (
                    <tr key={k} className="border-b border-[#0E0E0E]/10"><td className="py-3 uppercase tracking-[0.2em] text-[#0E0E0E]/45 w-32">{k}</td><td className="py-3" dangerouslySetInnerHTML={{ __html: v }} /></tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-8 flex items-end justify-between border-t border-[#0E0E0E]/15 pt-6">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#0E0E0E]/55 mb-1">Price</p>
                  <p className="font-serif text-3xl text-[#7B2D26]">CHF {active.price.toLocaleString()}</p>
                </div>
                <button className="bg-[#0E0E0E] text-[#F2EBDA] font-mono text-[10px] tracking-[0.3em] uppercase px-6 py-3 hover:bg-[#7B2D26] transition-colors">Reserve →</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, options, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-3 overflow-x-auto">
      <span className="text-[#0E0E0E]/45">{label}</span>
      <div className="flex gap-1">
        {options.map(o => (
          <button key={o} onClick={() => onChange(o)} data-testid={`${testId}-${o}`} className={`px-3 py-1.5 transition-colors whitespace-nowrap ${value === o ? 'bg-[#0E0E0E] text-[#F2EBDA]' : 'border border-[#0E0E0E]/15 hover:border-[#0E0E0E]/40'}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
